import {
  Button,
  TextField,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Box,
} from '@mui/material';
import theme from 'theme';
import _ from 'lodash';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Portlet,
  PortletToolbar,
  PortletContent,
  PortletFooter,
} from '../../../components/Portlets';
import { OOO_DESCRIPTION_MAX } from 'constants/fieldLimits';

const listRequestTypes = [
  'Sick Leave',
  'Conference',
  'Workshop',
  'Patron Saint Day',
  'Moving',
  'Wedding',
  'Funeral',
  'Other',
];

const OutOfOfficeForm = ({
  formProps: {
    values,
    errors,
    isValid,
    handleBlur,
    touched,
    dirty,
    setFieldValue,
    submitForm,
    handleChange,
    handleReset,
  },
}) => {
  const onSubmitButtonClick = async () => {
    await submitForm();
  };

  const onStartDateChange = (startDate, endDate) => {
    setFieldValue('startDate', startDate);
    if (!endDate) {
      endDate = startDate;
      setFieldValue('endDate', endDate);
    } else if (endDate < startDate) {
      endDate = startDate;
      setFieldValue('endDate', endDate);
    }
  };

  const onEndDateChange = (startDate, endDate) => {
    setFieldValue('endDate', endDate);

    if (endDate < startDate) {
      startDate = endDate;
      setFieldValue('startDate', startDate);
    }
  };

  const handleSelectChange = event => {
    setFieldValue('requestType', event.target.value);
  };

  return (
    <Portlet sx={{ borderRadius: '0 0 0.25rem 0.25rem' }} outlined={1} squared={false}>
      <PortletContent
        sx={{
          padding: '0.5rem 1.5rem',
          background: 'transparent',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          variant="body2"
          component="span"
          sx={{
            fontWeight: 600,
            marginBottom: theme.spacing(2),
            marginTop: theme.spacing(2),
            fontSize: '1rem',
          }}
        >
          Out of Office Request
        </Typography>
        <FormControl variant="standard" sx={{ minWidth: 120 }}>
          <InputLabel disabled id="select-outOfOfficeType-label">
            Type
          </InputLabel>
          <Select
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              getContentAnchorEl: null,
            }}
            labelId="select-outOfOfficeType-label"
            id="select-outOfOfficeType"
            value={values.requestType || ''}
            onChange={handleSelectChange}
            label="Type"
            data-cy="outOfOfficeType"
          >
            {listRequestTypes.map(listRequestType => (
              <MenuItem key={listRequestType} value={listRequestType}>
                {listRequestType}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <div>
            <DatePicker
              label="Start Date (inclusive)"
              value={values.startDate ?? null}
              onChange={value => onStartDateChange(value, values.endDate)}
              format="dd.MM.yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'standard',
                  margin: 'normal',
                  id: 'start-date',
                  name: 'startDate',
                  error: touched.startDate && Boolean(errors.startDate),
                  helperText: touched.startDate && errors.startDate ? errors.startDate : '',
                  onBlur: handleBlur,
                  onKeyDown: e => {
                    e.preventDefault();
                    return false;
                  },
                  slotProps: {
                    inputLabel: { shrink: true },
                  },
                },
                htmlInput: {
                  'data-cy': 'start-date-input',
                },
                popper: { 'data-cy': 'popover' },
                openPickerButton: {
                  'aria-label': 'change start date',
                  'data-cy': 'startDateIcon',
                },
                nextIconButton: { 'data-cy': 'startNextMonthSwitch' },
              }}
            />
          </div>
          {values.requestType === 'Patron Saint Day' ? null : (
            <div style={{ display: 'block' }}>
              <DatePicker
                label="End Date (inclusive)"
                value={values.endDate ?? null}
                onChange={value => onEndDateChange(values.startDate, value)}
                format="dd.MM.yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'standard',
                    margin: 'normal',
                    id: 'end-date',
                    name: 'endDate',
                    error: touched.endDate && Boolean(errors.endDate),
                    helperText: touched.endDate && errors.endDate ? errors.endDate : '',
                    onBlur: handleBlur,
                    onKeyDown: e => {
                      e.preventDefault();
                      return false;
                    },
                    slotProps: {
                      inputLabel: { shrink: true },
                    },
                  },
                  htmlInput: {
                    'data-cy': 'end-date-input',
                  },
                  popper: {
                    'data-cy': 'popover',
                  },
                  openPickerButton: {
                    'aria-label': 'change end date',
                    'data-cy': 'endDateIcon',
                  },
                }}
              />
            </div>
          )}

          <Box sx={{ marginBottom: theme.spacing(3) }}>
            <TextField
              fullWidth
              id="description"
              label="Description"
              name="description"
              variant="standard"
              placeholder="Enter a description"
              multiline
              maxRows="4"
              value={values.description || ''}
              onChange={handleChange}
              margin="normal"
              error={touched.description && Boolean(errors.description)}
              onBlur={handleBlur}
              helperText={
                (values.description || '').length >= OOO_DESCRIPTION_MAX
                  ? `Maximum length of ${OOO_DESCRIPTION_MAX} characters reached.`
                  : errors.description && touched.description
                    ? errors.description
                    : ''
              }
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { maxLength: OOO_DESCRIPTION_MAX },
              }}
            />
          </Box>
        </LocalizationProvider>
      </PortletContent>
      <PortletFooter sx={{ borderTop: 'none', marginTop: '20%' }}>
        <PortletToolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            disabled={!dirty}
            onClick={handleReset}
            variant="contained"
            sx={{
              bgcolor: 'primary.contrastText',
              color: 'common.black',
              '&:hover': { bgcolor: 'common.neutral' },
            }}
          >
            Reset
          </Button>
          <div>
            <Button
              disabled={_.isNil(values.endDate) || _.isNil(values.startDate) || !isValid}
              variant="contained"
              sx={{
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
              onClick={onSubmitButtonClick}
              data-cy="outOfOfficeSubmit"
            >
              Submit
            </Button>
          </div>
        </PortletToolbar>
      </PortletFooter>
    </Portlet>
  );
};

export default OutOfOfficeForm;
